// Meta Pixel: se carga SIEMPRE, sin esperar consentimiento de cookies
// (decisión del cliente: los píxeles no se bloquean por el banner).

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
    _fbq?: unknown;
    __vibraPixelInit?: boolean;
    __vibraPixelLoaded?: boolean;
  }
}

const PIXEL_ID = "1368179068542253";

type PixelParams = Record<string, unknown>;

// ─── Coincidencias avanzadas (Advanced Matching) ────────────────────────────
// Hasheamos email/teléfono en el navegador con SHA-256 antes de enviarlos a
// Meta. Nunca sale el dato en claro.

const USER_DATA_KEY = "vibra_pixel_user_v1";

type PixelUserData = {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  country?: string;
};

async function sha256(value: string): Promise<string> {
  const data = new TextEncoder().encode(value);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function normalizeEmail(v: string) {
  return v.trim().toLowerCase();
}
function normalizePhone(v: string) {
  const digits = v.replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.length === 9) return `34${digits}`;
  return digits;
}
function normalizeName(v: string) {
  return v.trim().toLowerCase();
}

function getStoredUserData(): PixelUserData | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(USER_DATA_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PixelUserData;
  } catch {
    return null;
  }
}

export async function setPixelUserData(input: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  country?: string;
}) {
  if (typeof window === "undefined") return;

  const data: PixelUserData = {};
  if (input.email) data.em = await sha256(normalizeEmail(input.email));
  if (input.phone) {
    const ph = normalizePhone(input.phone);
    if (ph) data.ph = await sha256(ph);
  }
  if (input.firstName) data.fn = await sha256(normalizeName(input.firstName));
  if (input.lastName) data.ln = await sha256(normalizeName(input.lastName));
  if (input.city) data.ct = await sha256(normalizeName(input.city));
  if (input.country) data.country = await sha256(input.country.trim().toLowerCase());

  try {
    window.sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }

  if (window.__vibraPixelLoaded && typeof window.fbq === "function") {
    window.fbq("init", PIXEL_ID, data, { autoConfig: true });
  }
}

export function clearPixelUserData() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(USER_DATA_KEY);
  } catch {
    /* ignore */
  }
}

function loadPixelScript() {
  if (typeof window === "undefined") return;
  if (window.__vibraPixelLoaded) return;
  window.__vibraPixelLoaded = true;

  // Snippet oficial de Meta, embebido para cargar inmediatamente.
  /* eslint-disable */
  (function (f: any, b: any, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e);
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */

  // Coincidencias Avanzadas:
  //  - Automáticas: Meta extrae email/teléfono de los formularios de la página
  //    y los envía hasheados (SHA-256) sin que viaje texto plano.
  //  - Manuales: si la app conoce email/teléfono (ej. tras un checkout),
  //    `setPixelUserData()` los hashea localmente y los pasa en el init.
  const userData = getStoredUserData();
  if (userData) {
    window.fbq?.("init", PIXEL_ID, userData, { autoConfig: true });
  } else {
    window.fbq?.("init", PIXEL_ID, {}, { autoConfig: true });
  }
  window.fbq?.("track", "PageView");
}

export function track(event: string, params?: PixelParams, eventID?: string) {
  if (typeof window === "undefined") return;
  if (!window.__vibraPixelLoaded) loadPixelScript();
  if (typeof window.fbq !== "function") return;
  const options = eventID ? { eventID } : undefined;
  if (params && options) window.fbq("track", event, params, options);
  else if (params) window.fbq("track", event, params);
  else if (options) window.fbq("track", event, {}, options);
  else window.fbq("track", event);
}

function matchesAny(value: string, patterns: RegExp[]) {
  return patterns.some((re) => re.test(value));
}

const CONTACT_HREF_PATTERNS = [
  /^https?:\/\/(www\.)?wa\.me\//i,
  /^https?:\/\/(api|chat)\.whatsapp\.com\//i,
  /^mailto:/i,
  /^tel:/i,
];

const CHECKOUT_HREF_PATTERNS = [
  /clientes\.vibramarketing\.es/i,
  /buy\.stripe\.com/i,
  /checkout\.stripe\.com/i,
  /paypal\.com\/checkout/i,
];

const CONTENT_ROUTES = [
  /^\/blog\/[^/]+/,
  /^\/noticias\/[^/]+/,
  /^\/guias\/[^/]+/,
  /^\/casos\/[^/]+/,
];

function trackPageView(pathname: string) {
  track("PageView");
  if (CONTENT_ROUTES.some((re) => re.test(pathname))) {
    track("ViewContent", { content_type: "article", content_name: pathname });
  }
  if (pathname.startsWith("/precios")) {
    track("ViewContent", { content_type: "pricing", content_name: "Precios" });
  }
}

function handleDocumentClick(ev: MouseEvent) {
  const target = ev.target as HTMLElement | null;
  if (!target) return;
  const anchor = target.closest("a") as HTMLAnchorElement | null;
  if (!anchor) return;
  const href = anchor.getAttribute("href") || "";
  if (!href) return;

  if (matchesAny(href, CONTACT_HREF_PATTERNS)) {
    track("Contact", { method: href.split(":")[0] || "link" });
    return;
  }
  if (matchesAny(href, CHECKOUT_HREF_PATTERNS)) {
    track("InitiateCheckout", { content_name: anchor.textContent?.trim() || href });
    return;
  }
}

export function initPixelTracking() {
  if (typeof window === "undefined") return;
  if (window.__vibraPixelInit) return;
  window.__vibraPixelInit = true;

  // Carga inmediata del píxel y PageView de la ruta actual.
  loadPixelScript();

  let lastPath = window.location.pathname;
  const fireOnChange = () => {
    const path = window.location.pathname;
    if (path === lastPath) return;
    lastPath = path;
    trackPageView(path);
  };

  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...args) {
    const ret = origPush.apply(this, args as Parameters<typeof history.pushState>);
    queueMicrotask(fireOnChange);
    return ret;
  };
  history.replaceState = function (...args) {
    const ret = origReplace.apply(this, args as Parameters<typeof history.replaceState>);
    queueMicrotask(fireOnChange);
    return ret;
  };
  window.addEventListener("popstate", fireOnChange);

  document.addEventListener("click", handleDocumentClick, { capture: true });
}
